import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useToast } from '../../components/Toast';

const Container = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
  padding-bottom: 80px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #222;
`;

// ... (Title, Badge, etc. remain the same) ...

// Inside StockPage component return:



const Title = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  background: #0072ff;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  color: #888;
  margin-bottom: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeroCard = styled.div`
  background: ${props => props.isRiskOn ? 'linear-gradient(135deg, #FF4B4B 0%, #a81c1c 100%)' : 'linear-gradient(135deg, #0072ff 0%, #004b9e 100%)'};
  padding: 24px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  margin-bottom: 30px;
`;

const HeroStatus = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #fff;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
`;

const HeroText = styled.p`
  font-size: 16px;
  color: rgba(255,255,255,0.9);
  line-height: 1.5;
  margin: 0;
  font-weight: 500;
`;

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.color || '#fff'};
`;

const CardBody = styled.p`
  font-size: 14px;
  color: #bbb;
  margin: 0;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const StatBox = styled.div`
  background: #222;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.color || '#fff'};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  background: ${props => props.active ? '#333' : '#111'};
  color: ${props => props.active ? '#fff' : '#666'};
  border: 1px solid ${props => props.active ? '#555' : '#222'};
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s;
`;

function StockPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSector, setSelectedSector] = useState(null);

  const [evaluation, setEvaluation] = useState(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await apiService.getStockReport('latest');
      if (res.success && res.data) {
        setData({ ...res.data.report, createdAt: res.data.createdAt });
      } else {
        showToast('리포트를 불러올 수 없습니다.', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast('데이터 로딩 실패', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRunEvaluation = async () => {
    if (!data) return;
    if (!window.confirm('현재 시장 뉴스와 비교하여 예측 정확도를 채점하시겠습니까?')) return;

    setLoading(true);
    try {
      showToast('시장 데이터를 분석하고 채점 중입니다...', 'info');
      const res = await apiService.runEvaluationBatch();
      if (res.success && res.result && res.result.evaluation) {
        setEvaluation(res.result.evaluation);
        showToast('채점 완료!', 'success');
        // Scroll to evaluation section
        setTimeout(() => {
          document.getElementById('evaluation-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } else {
        showToast('채점에 실패했습니다.', 'error');
      }
    } catch (e) {
      console.error(e);
      showToast('채점 중 오류 발생', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAnalysis = async () => {
    if (loading) return;
    setLoading(true);
    try {
      showToast('주식 분석을 시작했습니다. (약 1분 소요)', 'info');
      await apiService.runStockBatch();

      // Poll for result
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const res = await apiService.getStockReport('latest');
          if (res.success && res.data) {
            setData({ ...res.data.report, createdAt: res.data.createdAt });
            setEvaluation(null); // Reset evaluation on new analysis
          }
        } catch (e) {
          // Ignore
        }

        if (attempts > 20) {
          clearInterval(interval);
          setLoading(false);
          showToast('분석 완료 (또는 기존 데이터 표시)', 'success');
          fetchReport();
        }
      }, 3000);

    } catch (e) {
      setLoading(false);
      showToast('분석 시작 실패', 'error');
    }
  };

  const handleRefresh = async () => {
    if (window.confirm('새로운 배치를 실행하고 데이터를 갱신하시겠습니까?\n(약 1분 정도 소요됩니다)')) {
      handleRunAnalysis();
    }
  };

  if (loading) return (
    <Container style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <RefreshCw size={48} className="spin" style={{ marginBottom: '20px', color: '#0072ff' }} />
      <p>Market Alchemist is analyzing market data...</p>
      <p style={{ fontSize: '12px', color: '#666' }}>This may take up to 60 seconds.</p>
    </Container>
  );

  if (!data) return (
    <Container style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <TrendingUp size={64} style={{ marginBottom: '20px', color: '#333' }} />
      <h2>아직 생성된 리포트가 없습니다.</h2>
      <p style={{ color: '#888', marginBottom: '30px' }}>지금 바로 AI 분석을 실행하시겠습니까?</p>
      <button
        onClick={handleRunAnalysis}
        style={{
          background: '#0072ff',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer'
        }}
      >
        지금 분석 실행하기
      </button>
    </Container>
  );

  const isRiskOn = data.core_conclusion?.includes('Risk On') ||
    data.core_conclusion?.includes('상승') ||
    data.core_conclusion?.includes('반등');

  return (
    <Container>
      <Header>
        {/* Row 1: Navigation & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button onClick={() => navigate('/labs')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginRight: '10px', padding: 0 }}>
              <ArrowLeft size={24} />
            </button>
            <Title>Morning Brief <Badge>BETA</Badge></Title>
          </div>
          <button onClick={handleRefresh} style={{ background: 'none', border: 'none', color: '#0072ff', cursor: 'pointer', padding: '4px' }}>
            <RefreshCw size={20} />
          </button>
        </div>

        {/* Row 2: Info & Contextual Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {data && data.createdAt ? (
            <span style={{ fontSize: '12px', color: '#888' }}>
              {new Date(data.createdAt).toLocaleString('ko-KR', {
                month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
              })} 기준
            </span>
          ) : <span></span>}

          <button
            onClick={handleRunEvaluation}
            style={{
              background: '#222', border: '1px solid #444', color: '#e5e5e5',
              padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <span>✅</span> <span style={{ fontWeight: 600 }}>예측점검</span>
          </button>
        </div>
      </Header>

      {/* Evaluation Result Section */}
      {evaluation && (
        <Section id="evaluation-section">
          <SectionTitle>🎯 Prediction Accuracy Score: <span style={{ color: '#00ff88', marginLeft: '5px' }}>{evaluation.total_score}점</span></SectionTitle>
          <Card style={{ background: 'linear-gradient(135deg, #1a2a1a 0%, #111 100%)', border: '1px solid #00ff88' }}>
            <div style={{ fontSize: '15px', color: '#fff', marginBottom: '15px', lineHeight: '1.6' }}>
              "{evaluation.evaluation}"
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {evaluation.details?.map((detail, idx) => (
                <div key={idx} style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 'bold', color: '#ddd' }}>{detail.category}</span>
                    <span style={{
                      color: detail.result === 'Correct' || detail.result === 'Top' ? '#00ff88' : '#ff4b4b',
                      fontWeight: 'bold'
                    }}>
                      {detail.result}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#aaa' }}>{detail.comment}</div>
                </div>
              ))}
            </div>
            {evaluation.learning_point && (
              <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(0, 114, 255, 0.1)', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', color: '#0072ff', fontWeight: 'bold', marginBottom: '4px' }}>💡 Learning Point</div>
                <div style={{ fontSize: '13px', color: '#ccc' }}>{evaluation.learning_point}</div>
              </div>
            )}
          </Card>
        </Section>
      )}

      <HeroCard isRiskOn={isRiskOn}>

        <HeroStatus>{isRiskOn ? 'RISK ON' : 'CAUTION'}</HeroStatus>
        <HeroText>{data.core_conclusion}</HeroText>
      </HeroCard>

      <Grid style={{ marginBottom: '20px' }}>
        <StatBox>
          <StatLabel>KOSPI Range</StatLabel>
          <StatValue color="#ff4b4b">{data.index_range?.kospi}</StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>KOSDAQ Range</StatLabel>
          <StatValue color="#0072ff">{data.index_range?.kosdaq}</StatValue>
        </StatBox>
      </Grid>

      {/* Logic for Index Range */}
      <Card style={{ background: '#111', fontSize: '13px', color: '#888' }}>
        Logic: {data.index_range?.logic}
      </Card>

      <Section>
        <SectionTitle><TrendingUp size={16} /> Market Drivers</SectionTitle>
        {data.news_analysis?.positive?.map((item, idx) => (
          <Card key={idx}>
            <CardHeader color="#ff4b4b">🔥 호재: {item.news}</CardHeader>
            <CardBody>{item.flow_logic}</CardBody>
          </Card>
        ))}
      </Section>

      <Section>
        <SectionTitle><AlertTriangle size={16} /> Risk Factors</SectionTitle>
        {data.news_analysis?.risk?.map((item, idx) => (
          <Card key={idx}>
            <CardHeader color="#0072ff">❄️ 리스크: {item.news}</CardHeader>
            <CardBody>{item.outflow_logic}</CardBody>
          </Card>
        ))}
      </Section>

      {/* New Section: Sector Flow */}
      <Section>
        <SectionTitle>📊 Sector Flow</SectionTitle>
        <Grid>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ff4b4b', marginBottom: '8px' }}>🚀 강세 예상</div>
            {data.sector_flow?.bullish?.map((item, idx) => (
              <Card
                key={idx}
                onClick={() => setSelectedSector(item)}
                style={{ marginBottom: '8px', padding: '12px', cursor: 'pointer', transition: 'transform 0.2s', ':hover': { transform: 'scale(1.02)' } }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                  {item.sector} <span style={{ fontSize: '10px', color: '#666' }}>👉 상세</span>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>{item.reason}</div>
              </Card>
            ))}
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0072ff', marginBottom: '8px' }}>💧 약세 예상</div>
            {data.sector_flow?.bearish?.map((item, idx) => (
              <Card
                key={idx}
                onClick={() => setSelectedSector(item)}
                style={{ marginBottom: '8px', padding: '12px', cursor: 'pointer' }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                  {item.sector} <span style={{ fontSize: '10px', color: '#666' }}>👉 상세</span>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>{item.reason}</div>
              </Card>
            ))}
          </div>
        </Grid>
      </Section>

      {/* Sector Detail Modal */}
      {selectedSector && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', pading: '20px'
        }} onClick={() => setSelectedSector(null)}>
          <div style={{
            background: '#1a1a1a', width: '90%', maxWidth: '400px', maxHeight: '80vh',
            borderRadius: '16px', padding: '24px', overflowY: 'auto',
            border: '1px solid #333', boxShadow: '0 4px 30px rgba(0,0,0,0.5)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: '#fff' }}>{selectedSector.sector}</h3>
              <button
                onClick={() => setSelectedSector(null)}
                style={{ background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>ANALYST COMMENT</div>
              <div style={{ fontSize: '15px', color: '#ddd', lineHeight: '1.6' }}>{selectedSector.reason}</div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>LEADING STOCKS (대장주)</div>
              {selectedSector.leading_stocks && selectedSector.leading_stocks.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedSector.leading_stocks.map((stock, idx) => (
                    <div key={idx} style={{ background: '#252525', padding: '12px', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 'bold', color: '#fff' }}>{stock.name}</span>
                        {stock.code && <span style={{ fontSize: '11px', color: '#666' }}>{stock.code}</span>}
                      </div>
                      <div style={{ fontSize: '13px', color: '#aaa' }}>{stock.reason}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
                  추천 종목 데이터가 없습니다. (다음 분석부터 표시됩니다)
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Section: Retail Warnings */}
      {data.retail_warning && (
        <Section>
          <SectionTitle>⚠️ Retail Warnings (개미 주의보)</SectionTitle>
          <Card style={{ borderLeft: '3px solid #ffcc00' }}>
            {data.retail_warning.map((warn, idx) => (
              <div key={idx} style={{ marginBottom: '8px', fontSize: '14px', color: '#ddd' }}>
                • {warn}
              </div>
            ))}
          </Card>
        </Section>
      )}

      <Section>
        <SectionTitle>Scenarios & Strategy</SectionTitle>
        <TabContainer>
          {data.scenarios?.map((s, idx) => (
            <Tab key={idx} active={activeTab === idx} onClick={() => setActiveTab(idx)}>
              {s.name}
            </Tab>
          ))}
        </TabContainer>

        {data.scenarios && data.scenarios[activeTab] && (
          <Card>
            <CardHeader>{data.scenarios[activeTab].subtitle}</CardHeader>
            <CardBody style={{ whiteSpace: 'pre-line' }}>
              {data.scenarios[activeTab].strategy}
            </CardBody>
          </Card>
        )}
      </Section>

      <Section>
        <SectionTitle>Checkpoints</SectionTitle>
        <Card>
          <div style={{ marginBottom: '10px' }}><strong>Opening (9:00-9:30):</strong> {data.checkpoints?.opening}</div>
          <div style={{ marginBottom: '10px' }}><strong>Mid-day (10:00-11:00):</strong> {data.checkpoints?.mid}</div>
          <div><strong>Closing:</strong> {data.checkpoints?.closing}</div>
        </Card>
      </Section>

    </Container>
  );
}

export default StockPage;
